import { useState } from "react";
import { Upload, X, ArrowLeft, ArrowRight, FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { imgUrl, isPdf, uploadFiles, formatApiError } from "../../lib/api";

export const PageTitle = ({ title, subtitle, action }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
    <div><h1 className="font-serif text-3xl md:text-4xl font-light tracking-tight text-ink">{title}</h1>{subtitle && <p className="text-sm text-stone mt-1">{subtitle}</p>}</div>
    {action}
  </div>
);

export const Card = ({ children, className = "", ...rest }) => <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`} {...rest}>{children}</div>;

export const F = ({ label, name, value, onChange, type = "text", as = "input", hint, rows = 4, options, testId }) => (
  <div>
    <label htmlFor={name} className="block text-xs font-medium text-ink/80 mb-1.5">{label}</label>
    {as === "textarea" ? (
      <Textarea id={name} rows={rows} value={value ?? ""} onChange={(e) => onChange(name, e.target.value)} data-testid={testId || `admin-field-${name}`} className="bg-white" />
    ) : as === "select" ? (
      <select id={name} value={value ?? ""} onChange={(e) => onChange(name, e.target.value)} data-testid={testId || `admin-field-${name}`} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <Input id={name} type={type} value={value ?? ""} onChange={(e) => onChange(name, type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} data-testid={testId || `admin-field-${name}`} className="bg-white" />
    )}
    {hint && <p className="text-[11px] text-stone mt-1">{hint}</p>}
  </div>
);

export const Toggle = ({ label, name, checked, onChange, hint }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <div><div className="text-sm text-ink">{label}</div>{hint && <div className="text-[11px] text-stone">{hint}</div>}</div>
    <Switch checked={!!checked} onCheckedChange={(v) => onChange(name, v)} data-testid={`admin-toggle-${name}`} />
  </div>
);

export const MediaList = ({ label, name, value = [], onChange, folder = "projects", accept = "image/*", hint }) => {
  const [busy, setBusy] = useState(false);
  const move = (i, d) => { const a = [...value]; const [x] = a.splice(i, 1); a.splice(i + d, 0, x); onChange(name, a); };
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try { onChange(name, [...value, ...(await uploadFiles(files, { admin: true, folder }))]); toast.success(`${files.length} file caricati`); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><label className="text-xs font-medium text-ink/80">{label}</label>{hint && <span className="text-[11px] text-stone">{hint}</span>}</div>
      <div className="flex flex-wrap gap-3">
        {value.map((p, i) => (
          <div key={p + i} className="relative w-28 h-28 border border-gray-200 rounded-md overflow-hidden bg-gray-50 group">
            {isPdf(p) ? <div className="w-full h-full flex items-center justify-center text-stone"><FileText /></div> : <img src={imgUrl(p)} alt="" className="img-cover" />}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 disabled:opacity-30"><ArrowLeft size={12} /></button>
              <button type="button" onClick={() => onChange(name, value.filter((_, j) => j !== i))} className="p-1.5" data-testid={`admin-media-remove-${name}-${i}`}><X size={12} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === value.length - 1} className="p-1.5 disabled:opacity-30"><ArrowRight size={12} /></button>
            </div>
          </div>
        ))}
        <label className={`w-28 h-28 border border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center gap-1 text-[11px] text-stone cursor-pointer hover:border-gold ${busy ? "opacity-50" : ""}`}>
          <Upload size={16} />{busy ? "Carico…" : "Carica"}
          <input type="file" multiple accept={accept} className="hidden" onChange={pick} disabled={busy} data-testid={`admin-media-upload-${name}`} />
        </label>
      </div>
    </div>
  );
};

export const SingleImage = ({ label, name, value, onChange, folder = "projects" }) => (
  <div>
    <MediaList label={label} name={name} value={value ? [value] : []} onChange={(n, arr) => onChange(n, arr[arr.length - 1] || "")} folder={folder} />
    <Input value={value || ""} onChange={(e) => onChange(name, e.target.value)} placeholder="oppure incolla un URL immagine" className="mt-2 bg-white text-xs" data-testid={`admin-field-${name}-url`} />
  </div>
);

export const KeyValueEditor = ({ label, name, value = [], onChange, hint }) => {
  const upd = (i, k, v) => onChange(name, value.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><label className="text-xs font-medium text-ink/80">{label}</label>{hint && <span className="text-[11px] text-stone">{hint}</span>}</div>
      <div className="space-y-2">
        {value.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <Input value={r.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Etichetta (es. Investimento)" className="col-span-5 bg-white" data-testid={`admin-kv-label-${i}`} />
            <Input value={r.value} onChange={(e) => upd(i, "value", e.target.value)} placeholder="Valore (es. € 120.000)" className="col-span-4 bg-white" data-testid={`admin-kv-value-${i}`} />
            <label className="col-span-2 flex items-center gap-2 text-xs text-stone"><input type="checkbox" checked={!!r.public} onChange={(e) => upd(i, "public", e.target.checked)} data-testid={`admin-kv-public-${i}`} />Pubblico</label>
            <button type="button" onClick={() => onChange(name, value.filter((_, j) => j !== i))} className="col-span-1 text-stone hover:text-red-600"><Trash2 size={15} /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange(name, [...value, { label: "", value: "", public: false }])} className="inline-flex items-center gap-1 text-xs text-ink hover:text-gold" data-testid={`admin-kv-add-${name}`}><Plus size={14} /> Aggiungi voce</button>
      </div>
    </div>
  );
};
